import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { useDispatch, useSelector } from "react-redux"; // Import Redux hooks
import confetti from "canvas-confetti"; // Import confetti
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    Container,
    CircularProgress,
} from "@mui/material";
// Import step components
import AboutStep from "../../components/initial-profile-setup/AboutStep";
import ProfessionalStatusStep from "../../components/initial-profile-setup/ProfessionalStatusStep";
import ProfilePhotoStep from "../../components/initial-profile-setup/ProfilePhotoStep";
import LocationStep from "../../components/initial-profile-setup/LocationStep"; // Import the new step
// Import Redux state/actions
import { RootState, AppDispatch } from "../../store/store";
import {
    selectFullProfile,
    selectProfileLoading, // Import loading selector
    updateProfile, // Use the general updateProfile thunk
    fetchFullProfile, // Import fetchFullProfile thunk
    setTitle,
} from "../../store/slices/profileSlice"; // Import thunk and selectors
import { selectAuthToken } from "../../store/slices/authSlice"; // Remove selectUser import
import JoiningReasonStep from "../../components/initial-profile-setup/JoiningReason";
import { useTheme } from "../../context/ThemeContext";
import { useBackground } from "../../context/BackgroundContext";

const steps = ["Location", "About You", "Professional Status", "Joining Reason", "Profile Photo"]; // Added Location step

const GOLD_COLOR = "#9f8f5e";
const MINIMAL_GRADIENT_ID = "plain_gradient";

const InitialProfileSetupPage: React.FC = () => {
    const navigate = useNavigate(); // Hook for navigation
    const dispatch = useDispatch<AppDispatch>(); // Typed dispatch hook
    const [activeStep, setActiveStep] = useState(0);
    // Add location fields to profileData state
    const [profileData, setProfileData] = useState({
        country: "", // Added country
        state: "", // Added state
        city: "", // Added city
        title: "",
        about: "",
        professionalStatus: "",
        joiningReason: "",
    });
    const [isCompleting, setIsCompleting] = useState(false); // Loading state for completion
    const [error, setError] = useState<string | null>(null); // Error state
    const [isStepValid, setIsStepValid] = useState(false); // State for current step validity

    // Select necessary state from Redux
    const user = useSelector((state: RootState) => state.auth.user); // Correctly select user
    const fullProfile = useSelector(selectFullProfile); // Get the full profile object
    const token = useSelector(selectAuthToken); // Get auth token
    const profileLoading = useSelector(selectProfileLoading); // Get profile loading state
    const profileId = fullProfile?.id; // Extract profileId

    const { isDarkMode, toggleDarkMode, primaryColor, setPrimaryColor } = useTheme();
    const { selectedBackgroundId, setSelectedBackgroundId } = useBackground();

    useEffect(() => {
        const initialDarkMode = isDarkMode;
        const initialPrimaryColor = primaryColor;
        const initialBackgroundId = selectedBackgroundId;

        // Force light mode
        if (initialDarkMode) {
            toggleDarkMode();
        }

        // Force gold color theme
        if (initialPrimaryColor !== GOLD_COLOR) {
            setPrimaryColor(GOLD_COLOR);
        }

        // Force minimal gradient background
        if (initialBackgroundId !== MINIMAL_GRADIENT_ID) {
            setSelectedBackgroundId(MINIMAL_GRADIENT_ID);
        }

        // return () => {
        //     // Restore dark mode if it was the initial state
        //     // Read current state directly inside cleanup
        //     if (isDarkMode !== initialDarkMode) {
        //         toggleDarkMode();
        //     }
        //     // Restore primary color
        //     if (primaryColor !== initialPrimaryColor) {
        //         setPrimaryColor(initialPrimaryColor);
        //     }
        //     // Restore background
        //     if (selectedBackgroundId !== initialBackgroundId) {
        //         setSelectedBackgroundId(initialBackgroundId);
        //     }
        // };
    }, []);

    // Fetch full profile on mount if user is authenticated
    useEffect(() => {
        if (token) {
            dispatch(fetchFullProfile(token));
        }
    }, [dispatch, token]);

    // Redirect logic: Check profile status once loaded
    useEffect(() => {
        if (!profileLoading && fullProfile?.id) {
            // Check only when loading is done and profile ID exists
            if (fullProfile.initialSetupComplete) {
                // console.log("Initial setup already complete, redirecting to newsfeed...");
                navigate("/newsfeed");
            }
        }
    }, [profileLoading, fullProfile, navigate]); // Dependencies: loading state, profile object, navigate function

    // Set initial form data from profile if available (run only when profile is loaded and setup not complete)
    useEffect(() => {
        // Ensure profile is loaded and setup is NOT complete before setting form data
        if (!profileLoading && fullProfile?.id && !fullProfile.initialSetupComplete) {
            // console.log("Full profile loaded, setting initial form data:", fullProfile);
            setProfileData((prev) => ({
                ...prev,
                country: fullProfile.location?.country || "",
                state: fullProfile.location?.state || "",
                // city: fullProfile.location?.city || '', // Uncomment if city is added to ProfileState
                title: fullProfile.title || "",
                about: fullProfile.about || "",
                professionalStatus: fullProfile.professionalStatus || "",
                joiningReason: fullProfile.joiningReason || "",
            }));
        }
    }, [profileLoading, fullProfile]); // Simplified dependencies

    // Function to check if the current step's data is valid
    const validateStepData = (stepIndex: number): boolean => {
        switch (stepIndex) {
            case 0: // Location
                return !!profileData.country && !!profileData.state && !!profileData.city;
            case 1: // About
                return !!profileData.title && !!profileData.about;
            case 2: // Professional Status
                return !!profileData.professionalStatus;
            case 3: // Joining Reason
                return !!profileData.joiningReason;
            case 4: // Profile Photo (always valid to proceed from)
                return true;
            default:
                return false;
        }
    };

    // Effect to update step validity when data or step changes
    useEffect(() => {
        setIsStepValid(validateStepData(activeStep));
    }, [profileData, activeStep]);

    const handleNext = () => {
        // Prevent moving next if step is invalid (already checked by button disable, but good practice)
        if (!isStepValid) return;
        setError(null);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setError(null); // Clear error on step change
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleComplete = async () => {
        setError(null);
        if (!profileId || !token) {
            setError("Could not complete setup. User profile ID or token missing.");
            console.error("Profile ID or Token is missing", { profileId, token });
            return;
        }

        setIsCompleting(true);

        dispatch(setTitle(profileData.title));

        try {
            // Prepare data for the API call by merging with the full profile state
            const profileDataForUpdate = {
                ...fullProfile,
                location: {
                    country: profileData.country,
                    state: profileData.state,
                    city: profileData.city,
                },
                title: profileData.title,
                about: profileData.about,
                professionalStatus: profileData.professionalStatus,
                joiningReason: profileData.joiningReason,
                initialSetupComplete: true, // Mark setup as complete
            };

            // Dispatch the general updateProfile async thunk
            await dispatch(
                updateProfile({
                    token,
                    profileData: profileDataForUpdate,
                }),
            ).unwrap(); // unwrap() helps handle promise result/errors

            // console.log("Initial setup successfully updated!");

            // Trigger confetti!
            confetti({
                particleCount: 150,
                spread: 70,
                origin: { y: 0.6 },
            });

            // Short delay before navigating to let confetti show
            setTimeout(() => {
                navigate("/newsfeed"); // Navigate to newsfeed on success
            }, 1500); // Adjust delay as needed
        } catch (err: any) {
            console.error("Failed to complete initial setup:", err);
            setError(err.message || "An unexpected error occurred during setup completion.");
        } finally {
            setIsCompleting(false);
        }
    };

    // Update handleDataChange to handle location fields if needed (if LocationStep uses individual onChange)
    // Or handle it via a single location object update
    const handleLocationChange = (locationValue: {
        country: string;
        state: string;
        city: string;
    }) => {
        setProfileData((prevData) => ({
            ...prevData,
            ...locationValue,
        }));
    };

    const handleTitleChange = (titleValue: string) => {
        setProfileData((prevData) => ({ ...prevData, title: titleValue }));
    };

    const handleAboutChange = (aboutValue: string) => {
        setProfileData((prevData) => ({ ...prevData, about: aboutValue }));
    };

    const handleProfessionalStatusChange = (statusValue: string) => {
        setProfileData((prevData) => ({ ...prevData, professionalStatus: statusValue }));
    };

    const handleJoiningReasonChange = (joiningReasonValue: string) => {
        setProfileData((prevData) => ({ ...prevData, joiningReason: joiningReasonValue }));
    };

    const getStepContent = (step: number) => {
        switch (step) {
            case 0: // New Location Step
                return (
                    <LocationStep
                        value={{
                            country: profileData.country,
                            state: profileData.state,
                            city: profileData.city,
                        }}
                        onChange={handleLocationChange}
                    />
                );
            case 1: // About Step (index shifted)
                return (
                    <AboutStep
                        title={profileData.title}
                        about={profileData.about}
                        onTitleChange={handleTitleChange}
                        onAboutChange={handleAboutChange}
                    />
                );
            case 2: // Professional Status Step (index shifted)
                return (
                    <ProfessionalStatusStep
                        value={profileData.professionalStatus}
                        onChange={handleProfessionalStatusChange}
                    />
                );
            case 3: // Joining Reason Step (index shifted)
                return (
                    <JoiningReasonStep
                        value={profileData.joiningReason}
                        onChange={handleJoiningReasonChange}
                    />
                );
            case 4: // Profile Photo Step (index shifted)
                return <ProfilePhotoStep />;
            default:
                return <Typography>Unknown step</Typography>;
        }
    };

    // Loading state logic: Show loader if profile is loading OR if profile ID isn't loaded yet
    if (profileLoading || !fullProfile?.id) {
        return (
            <Container maxWidth="sm" sx={{ textAlign: "center", mt: 15 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading profile setup...</Typography>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="md"
            sx={{
                mt: 8,
                mb: 4,
                p: 4,
                borderRadius: 2,
                backgroundColor: `rgba(var(--card-background-rgb), 0.85)`,
                backdropFilter: "blur(8px)",
                boxShadow: `var(--card-shadow)`,
                border: `1px solid var(--border-color)`,
            }}
        >
            <Typography variant="h4" align="center" gutterBottom>
                Welcome! Let&apos;s set up your profile.
            </Typography>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Display Error Message */}
            {error && (
                <Typography color="error" align="center" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <Box
                sx={{
                    minHeight: "300px",
                    mb: 4,
                    p: 3,
                    position: "relative",
                }}
            >
                {/* Render the content for the current step */}
                {getStepContent(activeStep)}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button disabled={activeStep === 0 || isCompleting} onClick={handleBack}>
                    Back
                </Button>
                <Button
                    variant="contained"
                    onClick={activeStep === steps.length - 1 ? handleComplete : handleNext}
                    // Disable Next/Complete if completing OR if current step data is invalid (except for last step)
                    disabled={isCompleting || (activeStep !== steps.length - 1 && !isStepValid)}
                >
                    {activeStep === steps.length - 1 ? (
                        isCompleting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Complete Setup"
                        )
                    ) : (
                        "Next Step"
                    )}
                </Button>
            </Box>
        </Container>
    );
};

export default InitialProfileSetupPage;
